import { SEEDING_REQUIRED_CONSENT_CODES, type SeedingConsentCode } from '@musinsa/services-curator';
import { getCdnUrl } from '../../../../common/utils';

export type SeedingTermId =
  | 'campaignParticipation'
  | 'contentCopyright'
  | 'adDisclosure'
  | 'taxLevy'
  | 'serviceNotice';

export interface SeedingTermItem {
  id: SeedingTermId;
  /**
   * desktop 새 탭 약관 라우트(`/campaign/seeding/terms/[slug]`)의 URL 슬러그 (kebab).
   * 기존 `/terms`·`/policy` 처럼 약관을 목적별 개별 경로로 구분하기 위함.
   */
  slug: string;
  /** 동의 섹션 행 라벨 (예: '[필수] 캠페인 참여 및 콘텐츠 제작·게시 의무 동의'). */
  label: string;
  /** 약관 시트 상단(appbar) 타이틀. 필수 4종은 공통, 안내는 자체 타이틀. */
  sheetTitle: string;
  /** 필수 동의 여부 — `serviceNotice` 만 안내(체크박스 없음). */
  required: boolean;
  /** `public/html/seeding/` 하위 파일명. */
  htmlFile: string;
  /** 필수 항목의 BE 동의 코드 (안내 항목은 없음). */
  consentCode?: SeedingConsentCode;
}

/** 동의 섹션 노출 순서 (Figma node 2842-37419 기준). */
export const SEEDING_TERMS: SeedingTermItem[] = [
  {
    id: 'campaignParticipation',
    slug: 'campaign-participation',
    label: '[필수] 캠페인 참여 및 콘텐츠 제작·게시 의무 동의',
    sheetTitle: '캠페인 참여 동의',
    required: true,
    htmlFile: 'campaign-participation-agreement.html',
    consentCode: 'CAMPAIGN_PARTICIPATION_V1',
  },
  {
    id: 'contentCopyright',
    slug: 'content-copyright',
    label: '[필수] 콘텐츠 저작권에 관한 내용 동의',
    sheetTitle: '캠페인 참여 동의',
    required: true,
    htmlFile: 'content-copyright-agreement.html',
    consentCode: 'CONTENT_COPYRIGHT_V1',
  },
  {
    id: 'adDisclosure',
    slug: 'ad-disclosure',
    label: '[필수] 광고·표시(#광고) 의무 및 콘텐츠 가이드라인 준수에 동의',
    sheetTitle: '캠페인 참여 동의',
    required: true,
    htmlFile: 'ad-disclosure-guideline-agreement.html',
    consentCode: 'AD_GUIDELINE_V1',
  },
  {
    id: 'taxLevy',
    slug: 'tax-levy',
    label: '[필수] 제세공과금 관련 사항 동의',
    sheetTitle: '캠페인 참여 동의',
    required: true,
    htmlFile: 'tax-levy-agreement.html',
    consentCode: 'TAX_V1',
  },
  {
    id: 'serviceNotice',
    slug: 'service-notice',
    label: '[안내] 서비스 이용 관련 고지',
    sheetTitle: '캠페인 참여 동의',
    required: false,
    htmlFile: 'service-usage-notice.html',
  },
];

export const REQUIRED_SEEDING_TERMS = SEEDING_TERMS.filter((term) => term.required);
export const REQUIRED_SEEDING_TERM_IDS = REQUIRED_SEEDING_TERMS.map((term) => term.id);

/** URL 슬러그(kebab)로 약관 항목을 찾는다 — desktop 새 탭 라우트(`terms/[termId]`)에서 사용. */
export const getSeedingTermBySlug = (slug: string): SeedingTermItem | null =>
  SEEDING_TERMS.find((term) => term.slug === slug) ?? null;

// App 핸들러가 features 배럴에서 동의 코드 상수를 그대로 쓸 수 있도록 재노출.
export { SEEDING_REQUIRED_CONSENT_CODES, type SeedingConsentCode };

/**
 * 시딩 약관 본문(자세히) 렌더 스타일 override — Figma node 2814-32432.
 * 공용 `HtmlContentRenderer` 베이스(c4=16 semibold, c1=14 leading-relaxed)를 시딩 시트/새 탭에서만
 * 덮어쓴다. 베이스를 직접 바꾸면 큐레이터 가입 약관(`AgreementContent`)까지 회귀하므로 호출부 주입 방식.
 * - c4(섹션 타이틀) → Body/14_medium (14px / 500 / lh20 / black #000), 타이틀 row 상하 padding 12px
 *   (Figma node 2814-32436 = px-16 py-12). 베이스 `[&_p]:mb-3`(하단 마진만) 대신 `py-[12px]`+`my-0`
 *   으로 상·하 12px 를 타이틀 자체가 소유 → desktop 새 탭(래퍼 padding 없음)에서도 상단 여백 보장.
 * - c1(불릿 본문)   → Body/13_regular (13px / 400 / lh18 / gray-600 #666666)
 * - 불릿 마커(disc/circle) 색은 `<li>` 의 color 를 따르므로 `[&_li]:text-[#666666]` 로 함께 회색 처리.
 *   (Figma 픽셀 확인: 타이틀 #000, 본문·마커 #666666. gray-600 토큰은 MCDS 가 oklch 라 hex 직접 지정.)
 * twMerge 가 동일 속성 arbitrary variant 를 후자로 정리해 베이스 충돌 클래스를 대체한다.
 */
export const SEEDING_TERM_HTML_CLASS =
  '[&_.c4]:text-[14px] [&_.c4]:font-medium [&_.c4]:leading-[20px] [&_.c4]:text-black [&_.c4]:py-[12px]! [&_.c4]:my-0 ' +
  '[&_li]:text-[#666666] ' +
  '[&_.c1]:text-[13px] [&_.c1]:font-normal [&_.c1]:leading-[18px] [&_.c1]:text-[#666666]';

/**
 * 약관 정적 HTML 경로.
 * `getAgreementHtmlPath`(agreements) 와 동일한 local/CDN 분기.
 * 시딩 약관은 브랜드 구분 없는 통일 문서이므로 `unified/seeding/` 경로 사용.
 * local 환경에서는 Next.js `public/` 직접 참조, 그 외 CDN.
 */
export const getSeedingTermHtmlPath = (htmlFile: string): string => {
  if (process.env.NEXT_PUBLIC_SERVICE_ENV === 'local') {
    return `/html/unified/seeding/${htmlFile}`;
  }
  return getCdnUrl(`/html/unified/seeding/${htmlFile}`);
};
